import { SUPPORTED_CURRENCIES, type CurrencyCode } from "@majourneys/shared";
import { currencyForCountry } from "./geo";

/**
 * What the server knew about money when it rendered the page.
 *
 * WHY. Prices used to paint three times: first in MAD (the default the server
 * rendered with), then at the bundled fallback rate once the browser read the
 * guest's choice, then at the live rate once /currency/rates answered. A quad
 * ride read 32 EUR and then 33 EUR; on a hub page the last swap landed up to
 * 2.5 s in (traveller test, 2026-11-01). The server now picks the currency
 * (cookie, else country) and the live rates before it renders, and the browser
 * starts from exactly that snapshot, so the first paint is the final one.
 */
export type FxSnapshot = {
  currency: CurrencyCode;
  /** True when the guest picked this currency (the cookie), false when it was guessed from the country. */
  explicit: boolean;
  /** 1 MAD to code, margin already applied. Null when the server could not get live rates. */
  rates: Record<CurrencyCode, number> | null;
};

/**
 * The cookie that carries the guest's explicit pick to the server. Mirrors
 * localStorage `mj.currency`; the server cannot read localStorage, and without
 * this the first paint of every page was in the wrong currency.
 */
export const CURRENCY_COOKIE = "mj_currency";

/** The global the server-rendered page leaves for the browser to start from. */
export const FX_GLOBAL = "__MJ_FX";

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === "string" && (SUPPORTED_CURRENCIES as readonly string[]).includes(value);
}

/** Reads `mj_currency` out of a raw Cookie header. */
export function currencyFromCookieHeader(header: string | null | undefined): CurrencyCode | null {
  if (!header) return null;
  const match = new RegExp(`(?:^|;\\s*)${CURRENCY_COOKIE}=([A-Z]{3})`).exec(header);
  return match && isCurrencyCode(match[1]) ? match[1] : null;
}

/**
 * The currency a request renders in. The guest's own pick wins; with none, the
 * country Cloudflare reports picks it, exactly as the browser-side detection
 * does; with neither (local dev), dirhams.
 *
 * The edge cache key carries this value (server.ts), so a page rendered in EUR
 * is only ever served to a request that resolves to EUR. Four currencies means
 * at most four copies of a page, which is what makes it safe to bake into
 * shared HTML.
 */
export function resolveRequestCurrency(
  cookieCurrency: string | null | undefined,
  country: string | null | undefined,
): { currency: CurrencyCode; explicit: boolean } {
  if (isCurrencyCode(cookieCurrency)) return { currency: cookieCurrency, explicit: true };
  if (country) return { currency: currencyForCountry(country), explicit: false };
  return { currency: "MAD", explicit: false };
}

/**
 * Turns a /currency/rates body into a full rate table, or null when it holds
 * nothing usable. MAD is the base and stays 1; unknown codes are ignored. A
 * code the body lacks takes `fallback`'s value, or makes the whole table null
 * when no fallback is given (the server snapshot is all-live or nothing).
 */
export function ratesFromBody(
  body: unknown,
  fallback?: Record<CurrencyCode, number>,
): Record<CurrencyCode, number> | null {
  const rates = (body as { rates?: Record<string, unknown> } | null)?.rates;
  if (!rates || typeof rates !== "object") return null;
  const out = { MAD: 1 } as Record<CurrencyCode, number>;
  let live = 0;
  for (const code of SUPPORTED_CURRENCIES) {
    if (code === "MAD") continue;
    const rate = rates[code];
    if (typeof rate === "number" && rate > 0) {
      out[code] = rate;
      live += 1;
    } else if (fallback) out[code] = fallback[code];
    else return null;
  }
  return live > 0 ? out : null;
}

/** The script body that hands the snapshot to the browser. `<` is escaped so it can never close the tag. */
export function fxScript(snapshot: FxSnapshot): string {
  return `window.${FX_GLOBAL}=${JSON.stringify(snapshot).replace(/</g, "\\u003c")};`;
}

/** The snapshot the server left on the page, if any. */
export function readFxGlobal(): FxSnapshot | null {
  if (typeof window === "undefined") return null;
  const raw = (window as unknown as Record<string, unknown>)[FX_GLOBAL] as Partial<FxSnapshot> | undefined;
  if (!raw || !isCurrencyCode(raw.currency)) return null;
  return {
    currency: raw.currency,
    explicit: raw.explicit === true,
    rates: raw.rates ? ratesFromBody({ rates: raw.rates }) : null,
  };
}

/** Writes the guest's pick where the server can see it. A year, site-wide. */
export function writeCurrencyCookie(code: CurrencyCode): void {
  if (typeof document === "undefined") return;
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${CURRENCY_COOKIE}=${code}; path=/; max-age=31536000; samesite=lax${secure}`;
}
